/**
 * Coarse buckets for analytics properties (RFC-1577 §10: "IDs, enums, counts
 * and coarse buckets" — never raw text, and raw counts only where exact values
 * are the point). The scales are declared once in
 * schemas/analytics/fields/shared.json; use these helpers rather than ad-hoc
 * thresholds so every `*_bucket` field in the warehouse means the same thing.
 */

import type { CountBucket, CreditsBucket, LatencyBucket, LengthBucket } from './generated/analyticsEvents'

/** 0 · 1 · 2-3 · 4-5 · 6-10 · 11-20 · 21-50 · 51+ */
export function countBucket(n: number): CountBucket {
  if (!Number.isFinite(n) || n <= 0) return '0'
  if (n < 2) return '1'
  if (n <= 3) return '2-3'
  if (n <= 5) return '4-5'
  if (n <= 10) return '6-10'
  if (n <= 20) return '11-20'
  if (n <= 50) return '21-50'
  return '51+'
}

/** Character-length scale for text the event must NOT carry (e.g. a prompt). */
export function lengthBucket(chars: number): LengthBucket {
  if (!Number.isFinite(chars) || chars <= 0) return '0'
  if (chars <= 20) return '1-20'
  if (chars <= 50) return '21-50'
  if (chars <= 100) return '51-100'
  if (chars <= 250) return '101-250'
  if (chars <= 500) return '251-500'
  if (chars <= 1000) return '501-1000'
  return '1001+'
}

/** Wall-clock latency scale for terminal results. */
export function latencyBucket(ms: number): LatencyBucket {
  if (!Number.isFinite(ms) || ms < 1000) return 'lt_1s'
  if (ms < 3000) return '1-3s'
  if (ms < 10_000) return '3-10s'
  if (ms < 30_000) return '10-30s'
  if (ms < 60_000) return '30-60s'
  return 'gt_60s'
}

/** Credit amounts shown to the user: 0 · 1-100 · 101-500 · 501-1000 · 1001-5000 · 5001+. Never the exact amount. */
export function creditsBucket(credits: number): CreditsBucket {
  if (!Number.isFinite(credits) || credits <= 0) return '0'
  if (credits <= 100) return '1-100'
  if (credits <= 500) return '101-500'
  if (credits <= 1000) return '501-1000'
  if (credits <= 5000) return '1001-5000'
  return '5001+'
}
